import React, { Suspense, useLayoutEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useApiAuth } from "./context/api-auth";
import { Spinner, Box, Flex, Portal } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { detectLocaleFromPath, detectLocaleFromUrl, getPathWithoutLocale, supportedLocales } from "./i18n";

const AuthorizedApp = React.lazy(() => import("./Authorized"));
const UnauthorizedApp = React.lazy(() => import("./Unauthorized"));

const randomTexts = [
  "Hayat bir maceradır, riske atmazsanız sadece seyirci olursunuz.",
];

const getRandomText = () => {
  return randomTexts[Math.floor(Math.random() * randomTexts.length)];
};

export const LoadingScreen: React.FC = () => {
  return (
    <Portal>
      <Flex
        position="fixed"
        top="0"
        left="0"
        width="100vw"
        height="100vh"
        justify="center"
        align="center"
        zIndex={2}
      >
        <Box textAlign="center">
          <Spinner
            size="xl"
            color="purple.500"
          />
        </Box>
      </Flex>
    </Portal>
  );
};

/**
 * Global component that syncs i18n language with URL parameters
 * This ensures translation is activated immediately on first load and route changes
 */
function LocaleSync() {
  const { i18n } = useTranslation();
  const location = useLocation();
  
  // Read URL parameters immediately (both pathname and query params)
  const currentLocale = detectLocaleFromUrl(location.pathname, location.search);
  
  // Sync i18n language with URL locale immediately (before paint)
  useLayoutEffect(() => {
    if (i18n.language !== currentLocale) {
      i18n.changeLanguage(currentLocale);
    }
  }, [currentLocale, i18n, location.pathname, location.search]);
  
  return null;
}

/**
 * Global component that scrolls to top on route changes
 * This ensures users start at the top of the page when navigating internally
 */
function ScrollToTop() {
  const location = useLocation();
  
  // Scroll to top immediately when route changes (before paint)
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);
  
  return null;
}

function LocaleRouter() {
  const location = useLocation();
  const currentLocale = detectLocaleFromPath(location.pathname);
  const pathWithoutLocale = getPathWithoutLocale(location.pathname);
  const targetPath =
    pathWithoutLocale === '/'
      ? `/${currentLocale}`
      : `/${currentLocale}${pathWithoutLocale}`;

  if (location.pathname !== targetPath) {
    return <Navigate to={`${targetPath}${location.search}`} replace />;
  }

  return <AppContent />;
}

function AppContent() {
  const { user, loading } = useApiAuth();
  const location = useLocation();
  const currentLocale = detectLocaleFromPath(location.pathname);

  // Show loading screen while checking authentication
  if (loading) {
    return <LoadingScreen />;
  }

  // If user is authenticated, show authorized app
  if (user) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<Navigate to={`/${currentLocale}/dashboard`} replace />} />
          {supportedLocales.map(locale => (
            <Route key={locale} path={`${locale}/*`} element={<AuthorizedApp />} />
          ))}
          <Route path="*" element={<Navigate to={`/${currentLocale}/dashboard`} replace />} />
        </Routes>
      </Suspense>
    );
  }

  // If user is not authenticated, show unauthorized app
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<Navigate to={`/${currentLocale}`} replace />} />
        {supportedLocales.map(locale => (
          <Route key={locale} path={`${locale}/*`} element={<UnauthorizedApp />} />
        ))}
        <Route path="*" element={<Navigate to={`/${currentLocale}`} replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <>
      <LocaleSync />
      <ScrollToTop />
      <LocaleRouter />
    </>
  );
}